import { useEffect, useMemo, useRef, useState } from 'react';
import { scaleFor, pathLength, polygonArea, fmtLength, fmtArea, fmtElevation, elevationChange, toNatural } from './siteMeasureModel';
import { theme, Button, Segmented, STAGE } from './siteUi';
import { T } from '../../lib/bricks';

const MARK = T.brick;

const KIND_LABEL = { distance: 'Distance', area: 'Area', elevation: 'Elevation', rise: 'Elevation change' };

export function savedValueText(m) {
  if (m.kind === 'area') return fmtArea(m.value);
  if (m.kind === 'rise') return `${m.value > 0 ? '+' : m.value < 0 ? '−' : ''}${fmtElevation(Math.abs(m.value))}`;
  if (m.kind === 'elevation') return fmtElevation(m.value);
  return fmtLength(m.value);
}

// The figure on screen as a saved-figure body, or null.
export function figureFor(tool, points, readings, scale) {
  if (!scale || scale.error) return null;
  if (tool === 'distance' && points.length > 1) return { kind: 'distance', value: pathLength(points, scale), points: points.length };
  if (tool === 'area' && points.length > 2) return { kind: 'area', value: polygonArea(points, scale), points: points.length };
  if (tool === 'elevation') {
    const ok = readings.filter((r) => Number.isFinite(r.value));
    if (ok.length > 1) return { kind: 'rise', value: ok[ok.length - 1].value - ok[ok.length - 2].value, points: 2 };
    if (ok.length === 1 && readings[readings.length - 1] === ok[0]) return { kind: 'elevation', value: ok[0].value, points: 1 };
  }
  return null;
}

// Measure on a map picture: distance, area, and (DSM only) elevation at a point.
// onSaveMeasure(deliverableId, body) -> Promise<measures[]> and
// onRemoveMeasure(deliverableId, id) -> Promise<measures[]> keep figures for
// progress reports; the builder's timeline passes them, share pages don't.
// loadMapPreview(deliverableId) -> Promise<src>: a GeoTIFF with no uploaded
// preview (deliverable.preview_render) is drawn by the server; its scale is
// the file's own georeferencing (deliverable.measure).
export default function MeasureView({ deliverable, sampleElevation, loadMapPreview, onClose, tone = 'light', onSaveMeasure, onRemoveMeasure }) {
  const t = theme(tone);
  const m = deliverable.measure;
  const imgRef = useRef(null);
  const drawn = !deliverable.preview_url && deliverable.preview_render;
  const [src, setSrc] = useState(drawn ? null : deliverable.preview_url);
  const [drawErr, setDrawErr] = useState('');
  useEffect(() => {
    if (!drawn) return undefined;
    let live = true;
    if (!loadMapPreview) { setDrawErr('This map can’t be drawn here. Download the file to measure it.'); return undefined; }
    loadMapPreview(deliverable.id).then(
      (s) => { if (live) { if (s) setSrc(s); else setDrawErr('Couldn’t draw this map right now. Try again.'); } },
      (ex) => { if (live) setDrawErr((ex && ex.message) || 'Couldn’t draw this map right now. Try again.'); },
    );
    return () => { live = false; };
  }, [drawn, deliverable.id, loadMapPreview]);
  const [nat, setNat] = useState(null);
  const tools = [['distance', 'Distance'], ['area', 'Area']];
  if (m && m.elevation && sampleElevation) tools.push(['elevation', 'Elevation']);
  const [tool, setTool] = useState(m && m.elevation && sampleElevation ? 'elevation' : 'distance');
  const [points, setPoints] = useState([]);
  const [readings, setReadings] = useState([]);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(deliverable.measures || []);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');

  const scale = useMemo(() => (nat ? scaleFor(m, nat.w, nat.h) : null), [m, nat]);
  const off = scale && scale.error;

  const pick = async (e) => {
    if (!nat || off) return;
    const p = toNatural(e.clientX, e.clientY, imgRef.current.getBoundingClientRect(), nat.w, nat.h);
    if (!p) return;
    if (tool !== 'elevation') { setPoints((ps) => [...ps, p]); return; }
    const id = Date.now();
    setReadings((rs) => [...rs.slice(-4), { id, x: p.x, y: p.y, value: undefined }]);
    setErr('');
    try {
      const r = await sampleElevation(deliverable.id, Number(p.fx.toFixed(6)), Number(p.fy.toFixed(6)));
      const v = r && r.elevation_m !== null && r.elevation_m !== undefined ? Number(r.elevation_m) : null;
      setReadings((rs) => rs.map((x) => (x.id === id ? { ...x, value: v } : x)));
    } catch (ex) {
      setReadings((rs) => rs.filter((x) => x.id !== id));
      setErr((ex && ex.message) || "Couldn't read the elevation.");
    }
  };

  const switchTool = (t) => { setTool(t); setPoints([]); setReadings([]); setErr(''); setNote(''); };
  const figure = onSaveMeasure ? figureFor(tool, points, readings, scale) : null;

  async function save() {
    if (!figure) return;
    setSaving(true);
    setErr('');
    try {
      const list = await onSaveMeasure(deliverable.id, { ...figure, value: Number(figure.value.toFixed(3)), label: name.trim() || null });
      setSaved(list || []);
      setName('');
      setNote('Saved. It appears under Measurements in progress reports that include this visit.');
    } catch (ex) {
      setErr((ex && ex.message) || "Couldn't save the figure; nothing was saved.");
    }
    setSaving(false);
  }

  async function remove(id) {
    setErr('');
    try {
      setSaved((await onRemoveMeasure(deliverable.id, id)) || []);
      setNote('');
    } catch (ex) {
      setErr((ex && ex.message) || "Couldn't remove the figure.");
    }
  }

  const r = nat ? Math.max(nat.w, nat.h) / 140 : 4;
  let readout = '';
  if (scale && !off) {
    if (tool === 'distance') readout = points.length > 1 ? fmtLength(pathLength(points, scale)) : 'Tap two or more points.';
    if (tool === 'area') readout = points.length > 2 ? fmtArea(polygonArea(points, scale)) : 'Tap three or more corners.';
    if (tool === 'elevation') {
      const last = readings[readings.length - 1];
      readout = !last ? 'Tap a point.' : last.value === undefined ? 'Reading…' : fmtElevation(last.value);
      const ch = elevationChange(readings);
      if (ch && last && last.value !== undefined) readout += ` · ${ch} from the last point`;
    }
  }

  return (
    <div data-testid="site-measure" style={{ marginTop: 8, paddingTop: 20, borderTop: `1px solid ${t.hair}` }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <Segmented tone={tone} label="Measure" value={tool} onChange={switchTool} options={tools.map(([k, label]) => ({ value: k, label }))} />
        <Button tone={tone} variant="secondary" compact onClick={() => (tool === 'elevation' ? setReadings((x) => x.slice(0, -1)) : setPoints((x) => x.slice(0, -1)))}>Undo</Button>
        <Button tone={tone} variant="secondary" compact onClick={() => { setPoints([]); setReadings([]); }}>Clear</Button>
        <span style={{ flex: '1 1 auto' }} />
        <Button tone={tone} variant="tertiary" compact icon="download" href={deliverable.url} aria-label={`Download ${deliverable.label}`} />
        <Button tone={tone} variant="primary" compact onClick={onClose}>Done</Button>
      </div>
      <div style={{ position: 'relative', width: '100%', maxWidth: 960, background: STAGE.bg, borderRadius: 2, overflow: 'hidden', touchAction: 'manipulation' }}>
        {!src ? (
          <p data-testid="measure-drawing" role={drawErr ? 'alert' : 'status'} style={{ margin: 0, padding: '48px 16px', textAlign: 'center', fontSize: 14, color: drawErr ? STAGE.ink : STAGE.mute }}>
            {drawErr || 'Drawing the map from its file…'}
          </p>
        ) : null}
        {src ? <img
          ref={imgRef}
          src={src}
          alt={deliverable.label}
          onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          onClick={pick}
          style={{ display: 'block', width: '100%', height: 'auto', cursor: off ? 'default' : 'crosshair', userSelect: 'none' }}
          draggable={false}
        /> : null}
        {nat ? (
          <svg viewBox={`0 0 ${nat.w} ${nat.h}`} preserveAspectRatio="none" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            {tool === 'area' && points.length > 2 ? (
              <polygon points={points.map((p) => `${p.x},${p.y}`).join(' ')} fill={`${MARK}33`} stroke={MARK} strokeWidth={2} vectorEffect="non-scaling-stroke" />
            ) : null}
            {tool !== 'elevation' && points.length > 1 && tool === 'distance' ? (
              <polyline points={points.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={MARK} strokeWidth={2} vectorEffect="non-scaling-stroke" />
            ) : null}
            {(tool === 'elevation' ? readings : points).map((p, i) => (
              <circle key={p.id || i} cx={p.x} cy={p.y} r={r} fill="#fff" stroke={MARK} strokeWidth={2} vectorEffect="non-scaling-stroke" />
            ))}
          </svg>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 12, fontSize: 14 }}>
        {off ? <span style={{ color: t.mute }}>{scale.error}</span> : <span data-testid="measure-readout" style={{ fontWeight: 500, color: t.ink, fontVariantNumeric: 'tabular-nums' }}>{readout}</span>}
        {err ? <span role="alert" style={{ color: t.bad, fontSize: 13 }}>{err}</span> : null}
        {scale && !off ? (
          <span style={{ color: t.mute, fontSize: 12.5 }}>
            {scale.source === 'geotiff' ? 'Scale from the map file' : 'Scale set by Bricks'}. Not survey-grade.
          </span>
        ) : null}
      </div>
      {figure ? (
        <div data-testid="measure-save" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, maxWidth: 560 }}>
          <input aria-label="Name for this figure (optional)" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
            placeholder="Name, e.g. footing excavation (optional)"
            style={{ flex: '1 1 220px', minWidth: 0, minHeight: 44, padding: '0 12px', fontSize: 14, borderRadius: 2, border: `1px solid ${t.hairS}`, background: 'transparent', color: t.ink, boxSizing: 'border-box' }} />
          <Button tone={tone} variant="secondary" compact onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save to report'}</Button>
        </div>
      ) : null}
      {note ? <p role="status" style={{ margin: '8px 0 0', fontSize: 12.5, color: t.mute }}>{note}</p> : null}
      {onSaveMeasure && saved.length ? (
        <div data-testid="measure-saved" style={{ marginTop: 16, maxWidth: 560 }}>
          <div style={{ fontSize: 12.5, color: t.mute, marginBottom: 4 }}>Saved for reports</div>
          {saved.map((x) => (
            <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: `1px solid ${t.hair}`, minHeight: 44 }}>
              <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: 13.5, color: t.ink, overflowWrap: 'anywhere' }}>{x.label || KIND_LABEL[x.kind] || 'Figure'}</span>
              <span style={{ fontSize: 13.5, fontVariantNumeric: 'tabular-nums', color: t.ink, whiteSpace: 'nowrap' }}>{savedValueText(x)}</span>
              {onRemoveMeasure ? (
                <button type="button" onClick={() => remove(x.id)} aria-label={`Remove ${x.label || KIND_LABEL[x.kind] || 'figure'}`}
                  style={{ minWidth: 44, minHeight: 44, background: 'none', border: 'none', color: t.mute, fontSize: 13, cursor: 'pointer', padding: 0 }}>Remove</button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
